import { Box, Button, Menu, MenuItem, Stack } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { ManageUserColumnData } from "../../common/constants/gridRowColumn";
import { useUser } from "../../common/hooks/user";
import { SearchBar } from "../../components/SearchBar";
import { useDebounce } from "../../components/useDebounce";
import { layoutStyle } from "../layout-ui/style";
import AddMangeUser from "./AddMangeUser";
import { DeleteDialog } from "../../components/DeleteDialog";
import { TruckLoader } from "./truckLoader";
import {
  actionColumnFunction,
  checkbox,
  formatUserType,
  getDefaultColDef,
  isAdmin,
} from "../../common/constants/commonFunction";

const isUserStatusActive = (status) =>
  status === true ||
  status === 0 ||
  status === "0" ||
  status === "true" ||
  status === "True" ||
  status === "Active" ||
  status === "active";

export const ManageUser = () => {
  const classes = layoutStyle();
  const navigation = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [columnFinal, setColumnFinal] = useState([]);
  const [anchorElExport, setAnchorElExport] = useState(null);
  const { getUser, user, deleteUser, loading, setUserData, updateUser } =
    useUser();
  const [editRowData, setEditRowData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [anchorElSearch, setAnchorElSearch] = useState(null);
  const [mainRowData, setMainRowData] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteValue, setDeleteValue] = useState();
  const defaultColDef = useMemo(() => getDefaultColDef(), []);

  const handleAddButton = () => {
    setOpen(true);
  };

  const handleClickExport = (event) => {
    setAnchorElExport(event.currentTarget);
  };

  const handleExportFile = (format) => {
    const rows = mainRowData?.map((row) => ({
      Code: row?.code || "",
      Name: row?.name || "",
      Email: row?.email || "",
      Username: row?.userName || row?.username || "",
      Password: row?.password || "",
      Phone: row?.phone || "",
      Type: formatUserType(row?.type) || row?.type || "",
      Status: isUserStatusActive(row?.status) ? "Active" : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Manage Users");
    const fileName = format === "csv" ? "ManageUsers.csv" : "ManageUsers.xlsx";
    XLSX.writeFile(workbook, fileName);
    setAnchorElExport(null);
  };

  const handleEditButton = (rowData) => {
    setEditRowData(rowData);
    setOpen(true);
  };
  const handleDeleteButton = async (values) => {
    setDeleteOpen(true);
    setDeleteValue(values?.id);
  };

  const handleDelete = async () => {
    await deleteUser(deleteValue);
    setDeleteOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setEditRowData(null);
    setUserData(null);
  };

  const handleStatusToggle = async (params) => {
    try {
      const newStatus = isUserStatusActive(params.data.status) ? 1 : 0;
      const payload = {
        id: params.data.id,
        status: newStatus,
      };
      await updateUser(payload);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  useEffect(() => {
    if (ManageUserColumnData) {
      const actionColumn = actionColumnFunction({
        selectedRows: selectedRows,
        handleEditButton: handleEditButton,
        handleDeleteButton: handleDeleteButton,
        deleteButton: isAdmin(),
      });

      // Create a custom status column with toggle functionality
      const statusColumnWithToggle = ManageUserColumnData.map((col) => {
        if (col.field === "status") {
          return {
            ...col,
            cellRenderer: (params) => (
              <Box>
                {isUserStatusActive(params.data.status)
                  ? "Active"
                  : "Inactive"}
              </Box>
            ),
          };
        }
        return col;
      });

      setColumnFinal([checkbox, ...statusColumnWithToggle, actionColumn]);
    }
  }, [ManageUserColumnData, selectedRows]);
  const onSearchFunc = (e) => {
    setSearchValue(e[0]);
  };
  const debouncedSearch = useDebounce(onSearchFunc, 500);
  const searchData = mainRowData?.filter((company) =>
    company?.name?.toLowerCase().includes(searchValue?.toLowerCase()),
  );
  const onSearch = (e) => {
    debouncedSearch(e?.target?.value);
    handleClickSearch(e);
  };
  const handleClickSearch = (event) => {
    setAnchorElSearch(event.currentTarget);
  };
  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    if (searchValue) {
      setMainRowData(searchData);
    } else if (user?.length > 0) {
      setMainRowData(user);
    }
  }, [searchValue, user]);

  return (
    <Box className={classes.mainContent}>
      <Box className={classes.gridContantFleetList}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Box className={classes.headerNameText}>Users</Box>
          <Box className={classes.addEditGrid}>
            <Button
              variant="outlined"
              onClick={handleAddButton}
              className={classes.fleetListNewButton}
            >
              Add
            </Button>
            <Button
              variant="outlined"
              onClick={handleClickExport}
              className={classes.fleetListNewButton}
            >
              Export
            </Button>
            <Menu
              anchorEl={anchorElExport}
              open={Boolean(anchorElExport)}
              onClose={() => setAnchorElExport(null)}
            >
              <MenuItem onClick={() => handleExportFile("xlsx")}>
                Export XLSX
              </MenuItem>
              <MenuItem onClick={() => handleExportFile("csv")}>
                Export CSV
              </MenuItem>
            </Menu>
            <SearchBar onSearch={onSearch} />
          </Box>
        </Stack>
        {loading ? (
          <TruckLoader />
        ) : (
          <AgGridReact
            className="ag-theme-quartz"
            onRowSelectionModelChange={(ids) => {
              setSelectedRows(ids);
            }}
            rowSelection="multiple"
            pinnedColumns={{ right: ["action"] }}
            pagination={true}
            rowData={mainRowData}
            defaultColDef={defaultColDef}
            columnDefs={columnFinal}
            checkboxSelection
          />
        )}
        <AddMangeUser
          setOpen={setOpen}
          open={open}
          rowData={editRowData}
          handleClose={() => handleClose()}
        />
      </Box>
      {deleteOpen && (
        <DeleteDialog
          open={deleteOpen}
          setOpen={() => setDeleteOpen(false)}
          deleteApi={() => handleDelete()}
        />
      )}
    </Box>
  );
};
